// src/services/documentService.js (Updated)
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  getDocs, 
  getDoc,
  query, 
  where, 
  orderBy,
  serverTimestamp 
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject 
} from 'firebase/storage';
import { db, storage } from '../config/firebase';
import { userService } from './userService';

const DOCUMENTS_COLLECTION = 'docs-documents';

export const documentService = {
  // Upload file to Firebase Storage
  async uploadFile(file, userId, folder = 'documents') {
    try {
      const timestamp = Date.now();
      const fileName = `${timestamp}_${file.name}`;
      const fileRef = ref(storage, `${folder}/${userId}/${fileName}`);
      
      const snapshot = await uploadBytes(fileRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      return {
        url: downloadURL,
        path: snapshot.ref.fullPath,
        name: fileName,
        originalName: file.name,
        size: file.size,
        type: file.type
      };
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  },

  // Create document record
  async createDocument(documentData, userId) {
    try {
      const docRef = await addDoc(collection(db, DOCUMENTS_COLLECTION), {
        ...documentData,
        userId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // Update user's document count and storage usage
      const userProfile = await userService.getUserProfile(userId);
      if (userProfile) {
        const newDocCount = (userProfile.subscription?.documentsCount || 0) + 1;
        const newStorageUsed = (userProfile.subscription?.storageUsed || 0) + documentData.size;
        
        await userService.updateStorageUsage(userId, newStorageUsed, newDocCount);
      }
      
      return docRef.id;
    } catch (error) {
      console.error('Error creating document:', error);
      throw error;
    }
  },

  // Get user documents
  async getUserDocuments(userId) {
    try {
      const q = query(
        collection(db, DOCUMENTS_COLLECTION),
        where('userId', '==', userId),
        orderBy('updatedAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching documents:', error);
      throw error;
    }
  },

  // Update document
  async updateDocument(documentId, updates) {
    try {
      const docRef = doc(db, DOCUMENTS_COLLECTION, documentId);
      await updateDoc(docRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating document:', error);
      throw error;
    }
  },

  // Delete document
  async deleteDocument(documentId, filePath, userId) {
    try {
      // Get document data first to update user storage
      const docRef = doc(db, DOCUMENTS_COLLECTION, documentId);
      const docSnapshot = await getDoc(docRef);
      
      if (docSnapshot.exists()) {
        const docData = docSnapshot.data();
        
        // Delete from Firestore
        await deleteDoc(docRef);
        
        // Delete from Storage if file path exists
        if (filePath) {
          const fileRef = ref(storage, filePath);
          await deleteObject(fileRef);
        }

        // Update user's document count and storage usage
        if (userId) {
          const userProfile = await userService.getUserProfile(userId);
          if (userProfile) {
            const newDocCount = Math.max(0, (userProfile.subscription?.documentsCount || 0) - 1);
            const newStorageUsed = Math.max(0, (userProfile.subscription?.storageUsed || 0) - (docData.size || 0));
            
            await userService.updateStorageUsage(userId, newStorageUsed, newDocCount);
          }
        }
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      throw error;
    }
  },

  // Get document by ID (with user verification)
  async getDocument(documentId, userId) {
    try {
      const docRef = doc(db, DOCUMENTS_COLLECTION, documentId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const docData = { id: docSnap.id, ...docSnap.data() };
        
        // Verify the user owns this document
        if (docData.userId !== userId) {
          throw new Error('Unauthorized access to document');
        }
        
        return docData;
      } else {
        throw new Error('Document not found');
      }
    } catch (error) {
      console.error('Error fetching document:', error);
      throw error;
    }
  }
};